import { Feature } from 'geojson';
import ukhabBaselineAttributes from './ukhab-baseline-attributes.json';
import ukhabBaselinePrimaryCodeColumns from './ukhab-baseline-primcode.json';
import { FeatureStyler } from '../map-features.service';
import * as mapboxgl from 'mapbox-gl/esm';


type COLOR = `#${string}`;
type UKHabL2 = 'c' | 'w' | 'f' | 'g' | 'u' | 't' | 's' | 'r' | 'h';

interface UKHabSybmologyStripes {
  type: 'stripes';
  backgroundColor?: string;
  color: string;
  lineWidth: number;
  angle: number;
  dash: boolean;
}

interface UKHabSybmologyDots {
  type: 'dots';
  backgroundColor?: string;
  color: string;
}

interface UKHabSybmologyRhombs {
  type: 'rhombs';
  backgroundColor?: string;
  color: string;
  border: boolean;
}

type UKHabSybmology = UKHabSybmologyStripes | UKHabSybmologyDots | UKHabSybmologyRhombs | string;

const ukHabL2Colors: Record<UKHabL2, COLOR> = {
  c: '#FF7E00',
  w: '#339f2c',
  f: '#fd7bee',
  g: '#97ef5d',
  u: '#eb2244',
  t: '#1b53d6',
  s: '#a7a7a4',
  r: '#29ecf5',
  h: '#fafc81'
};

const ukHabSybmology: [string, UKHabSybmology][] = [
  ['c1f', { type: 'rhombs', color: '#FFFFFF', backgroundColor: ukHabL2Colors['c'], border: false }],
  ['c1e', { type: 'rhombs', color: ukHabL2Colors['c'], backgroundColor: '#FFFFFF', border: false }],
  ['c1d', { type: 'dots', color: '#FFFFFF', backgroundColor: ukHabL2Colors['c'] }],
  ['c1c', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['c'], backgroundColor: '#FFFFFF', angle: 0, dash: false }],
  ['c1b', { type: 'stripes', lineWidth: 1, color: ukHabL2Colors['c'], backgroundColor: '#FFFFFF', angle: 90, dash: false }],
  ['c1a', { type: 'dots', color: ukHabL2Colors['c'], backgroundColor: '#FFFFFF' }],
  ['c1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['c'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['c', ukHabL2Colors['c']],

  ['f2e', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['f'], backgroundColor: '#FAFC81', angle: 0, dash: false }],
  ['f2d', { type: 'stripes', lineWidth: 1, color: '#FAFC81', backgroundColor: ukHabL2Colors['f'], angle: 90, dash: false }],
  ['f2a', { type: 'stripes', lineWidth: 4, color: '#FAFC81', backgroundColor: ukHabL2Colors['f'], angle: 45, dash: false }],
  ['f2', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['f'], backgroundColor: '#FAFC81', angle: 135, dash: false }],
  ['f1b', { type: 'stripes', lineWidth: 1, color: ukHabL2Colors['f'], backgroundColor: '#FFFFFF', angle: 90, dash: false }],
  ['f1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: ukHabL2Colors['f'], angle: 45, dash: false }],
  ['f1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['f'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['f', ukHabL2Colors['f']],

  ['g4', '#CBFFB6'],
  ['g3c8', { type: 'dots', color: '#CBFFB6', backgroundColor: ukHabL2Colors['g'] }],
  ['g3c7', { type: 'rhombs', color: ukHabL2Colors['g'], backgroundColor: '#FFFFFF', border: true }],
  ['g3c6', { type: 'rhombs', color: '#CBFFB6', backgroundColor: ukHabL2Colors['g'], border: false }],
  ['g3c5', { type: 'dots', color: ukHabL2Colors['g'], backgroundColor: '#CBFFB6' }],
  ['g3c', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['g'], backgroundColor: '#CBFFB6', angle: 0, dash: false }],
  ['g3b', { type: 'stripes', lineWidth: 1, color: '#CBFFB6', backgroundColor: ukHabL2Colors['g'], angle: 90, dash: false }],
  ['g3a', { type: 'stripes', lineWidth: 4, color: '#CBFFB6', backgroundColor: ukHabL2Colors['g'], angle: 45, dash: false }],
  ['g3', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['g'], backgroundColor: '#CBFFB6', angle: 135, dash: false }],
  ['g2b', { type: 'stripes', lineWidth: 1, color: '#FAFC81', backgroundColor: ukHabL2Colors['g'], angle: 90, dash: false }],
  ['g2a', { type: 'stripes', lineWidth: 4, color: '#FAFC81', backgroundColor: ukHabL2Colors['g'], angle: 45, dash: false }],
  ['g2', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['g'], backgroundColor: '#FAFC81', angle: 135, dash: false }],
  ['g1c', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['g'], backgroundColor: '#FFFFFF', angle: 0, dash: false }],
  ['g1b6', { type: 'rhombs', color: '#FAFC81', backgroundColor: ukHabL2Colors['g'], border: false }],
  ['g1b5', { type: 'dots', color: ukHabL2Colors['g'], backgroundColor: '#FAFC81' }],
  ['g1b', { type: 'stripes', lineWidth: 1, color: ukHabL2Colors['g'], backgroundColor: '#FFFFFF', angle: 90, dash: false }],
  ['g1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: ukHabL2Colors['g'], angle: 45, dash: false }],
  ['g1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['g'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['g', ukHabL2Colors['g']],

  ['h3', { type: 'stripes', lineWidth: 4, color: '#EB2244', backgroundColor: '#8268D6', angle: 135, dash: false }],
  ['h2b', { type: 'stripes', lineWidth: 2, color: '#EB2244', backgroundColor: '#8268D6', angle: 0, dash: true }],
  ['h2a', { type: 'stripes', lineWidth: 2, color: '#000000', backgroundColor: '#8268D6', angle: 0, dash: true }],
  ['h2', { type: 'stripes', lineWidth: 2, color: '#FAFC81', backgroundColor: '#8268D6', angle: 0, dash: true }],
  ['h1b6', { type: 'rhombs', color: '#FFFFFF', backgroundColor: '#8268D6', border: false }],
  ['h1b5', { type: 'dots', color: '#8268D6', backgroundColor: '#FFFFFF' }],
  ['h1b', { type: 'stripes', lineWidth: 1, color: '#8268D6', backgroundColor: '#FFFFFF', angle: 90, dash: false }],
  ['h1a7', { type: 'rhombs', color: '#8268D6', backgroundColor: '#FFFFFF', border: false }],
  ['h1a5', { type: 'dots', color: '#FFFFFF', backgroundColor: '#8268D6' }],
  ['h1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: '#8268D6', angle: 45, dash: false }],
  ['h1', { type: 'stripes', lineWidth: 4, color: '#8268D6', backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['h', ukHabL2Colors['h']],

  ['r2', { type: 'dots', color: '#FAFC81', backgroundColor: ukHabL2Colors['r'] }],
  ['r1e', { type: 'dots', color: ukHabL2Colors['r'], backgroundColor: '#FFFFFF' }],
  ['r1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: ukHabL2Colors['r'], angle: 45, dash: false }],
  ['r1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['r'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['r', ukHabL2Colors['r']],

  ['s3b', { type: 'stripes', lineWidth: 1, color: '#CFCFCA', backgroundColor: ukHabL2Colors['s'], angle: 90, dash: false }],
  ['s3a', { type: 'stripes', lineWidth: 4, color: '#CFCFCA', backgroundColor: ukHabL2Colors['s'], angle: 45, dash: false }],
  ['s3', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['s'], backgroundColor: '#CFCFCA', angle: 135, dash: false }],
  ['s2a', { type: 'stripes', lineWidth: 4, color: '#FAFC81', backgroundColor: ukHabL2Colors['s'], angle: 45, dash: false }],
  ['s2', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['s'], backgroundColor: '#FAFC81', angle: 135, dash: false }],
  ['s1d', { type: 'dots', color: ukHabL2Colors['s'], backgroundColor: '#FFFFFF' }],
  ['s1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: ukHabL2Colors['s'], angle: 45, dash: false }],
  ['s1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['s'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['s', ukHabL2Colors['s']],

  ['t2d', { type: 'dots', color: ukHabL2Colors['t'], backgroundColor: '#FAFC81' }],
  ['t2a', { type: 'stripes', lineWidth: 4, color: '#FAFC81', backgroundColor: ukHabL2Colors['t'], angle: 45, dash: false }],
  ['t2', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['t'], backgroundColor: '#FAFC81', angle: 135, dash: false }],
  ['t1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['t'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['t', ukHabL2Colors['t']],

  ['u1e', '#F36F85'],
  ['u1d', { type: 'rhombs', color: ukHabL2Colors['u'], backgroundColor: '#FFFFFF', border: false }],
  ['u1c', { type: 'dots', color: '#FFFFFF', backgroundColor: ukHabL2Colors['u'] }],
  ['u1b6', { type: 'rhombs', color: '#FFFFFF', backgroundColor: ukHabL2Colors['u'], border: false }],
  ['u1b5', { type: 'dots', color: ukHabL2Colors['u'], backgroundColor: '#FFFFFF' }],
  ['u1b', { type: 'stripes', lineWidth: 1, color: '#FFFFFF', backgroundColor: ukHabL2Colors['u'], angle: 90, dash: false }],
  ['u1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: ukHabL2Colors['u'], angle: 45, dash: false }],
  ['u1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['u'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['u', ukHabL2Colors['u']],

  ['w2c', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['w'], backgroundColor: '#FAFC81', angle: 0, dash: false }],
  ['w2b', { type: 'stripes', lineWidth: 1, color: '#FAFC81', backgroundColor: ukHabL2Colors['w'], angle: 90, dash: false }],
  ['w2a', { type: 'stripes', lineWidth: 4, color: '#FAFC81', backgroundColor: ukHabL2Colors['w'], angle: 45, dash: false }],
  ['w2', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['w'], backgroundColor: '#FAFC81', angle: 135, dash: false }],
  ['w1h', { type: 'dots', color: ukHabL2Colors['w'], backgroundColor: '#A94900' }],
  ['w1g6', { type: 'stripes', lineWidth: 2, color: '#000000', backgroundColor: ukHabL2Colors['w'], angle: 90, dash: true }],
  ['w1g', { type: 'dots', color: '#A94900', backgroundColor: ukHabL2Colors['w'] }],
  ['w1f', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['w'], backgroundColor: '#A94900', angle: 0, dash: false }],
  ['w1e', { type: 'stripes', lineWidth: 1, color: '#A94900', backgroundColor: ukHabL2Colors['w'], angle: 90, dash: false }],
  ['w1d', { type: 'stripes', lineWidth: 4, color: '#A94900', backgroundColor: ukHabL2Colors['w'], angle: 45, dash: false }],
  ['w1c', { type: 'stripes', lineWidth: 2, color: ukHabL2Colors['w'], backgroundColor: '#FFFFFF', angle: 0, dash: false }],
  ['w1b', { type: 'stripes', lineWidth: 1, color: '#FFFFFF', backgroundColor: ukHabL2Colors['w'], angle: 90, dash: false }],
  ['w1a', { type: 'stripes', lineWidth: 4, color: '#FFFFFF', backgroundColor: ukHabL2Colors['w'], angle: 45, dash: false }],
  ['w1', { type: 'stripes', lineWidth: 4, color: ukHabL2Colors['w'], backgroundColor: '#FFFFFF', angle: 135, dash: false }],
  ['w', ukHabL2Colors['w']],
] as const;

const lineColorMap: [string, string, string][] = [
  ['f2d', '#fd7bee', '#f6f176'],

  ['g3c', '#97ef5d', '#cbfeb5'],
  ['g3c5', '#97ef5d', '#cbfeb5'],
  ['g3c6', '#97ef5d', '#cbfeb5'],
  ['g3c7', '#97ef5d', '#cbfeb5'],
  ['g3c8', '#97ef5d', '#cbfeb5'],

  ['h2', '#8167d6', '#f6f176'],

  ['h2a', '#8167d6', '#000000'],
  ['h2a5', '#8167d6', '#000000'],
  ['h2a6', '#8167d6', '#000000'],

  ['h2b', '#8167d6', '#ec2243'],

  ['r1e', '#29ecf5', '#f6f176'],
  ['u1e', '#ec2243', '#ec2243'],
  ['w1g', '#339f2c', '#A94900'],
  ['w1g6', '#339f2c', '#000000'],
] as const;

export class UKHabService {
  private constructor() { }

  public static instance: UKHabService = new UKHabService();

  ukhabPCode(data: Record<string, any>) {

    let pcode = '';
    for (const c of ukhabBaselinePrimaryCodeColumns) {
      if (data[ukhabBaselineAttributes[c]] && data[ukhabBaselineAttributes[c]].length > pcode.length) {
        pcode = data[ukhabBaselineAttributes[c]];
      }
    }
    return pcode ?? null;
  }

  matchUKHabSymbology(id: string) {
    if (!id) {
      return null;
    }

    const k = ukHabSybmology.find(([key]) => id.startsWith(key));
    return k ? k[0] : null;
  }

  generateUKHabFillSymbology(id: string) {
    const size = 20;
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d');

    const config = ukHabSybmology.find(([key]) => id.startsWith(key))?.[1];

    if (!config) {
      console.warn('Missing symbology config for ', id);
      ctx!.fillStyle = '#BBBBBB';
      ctx!.fillRect(0, 0, size, size);
      return ctx!.getImageData(0, 0, canvas.width, canvas.height);
    }

    if (typeof config === 'string') {
      ctx!.fillStyle = config;
      ctx!.fillRect(0, 0, size, size);
      return ctx!.getImageData(0, 0, canvas.width, canvas.height);
    }

    const center = size / 2;
    ctx!.fillStyle = config.backgroundColor ?? '#FFFFFF';
    ctx!.fillRect(0, 0, size, size);
    ctx!.fillStyle = ctx!.strokeStyle = config.color;
    ctx!.beginPath();

    switch (config.type) {
      case 'stripes': {
        const angle = config.angle ?? 0;
        const length = config.dash ? size / 2 : size * Math.sqrt(2);
        ctx!.lineWidth = size / config.lineWidth;
        ctx!.save();

        // if 135deg diagonal
        angle === 135 && ctx!.translate(size, 0);
        // draw main line
        ctx!.rotate((angle) * Math.PI / 180);
        ctx!.moveTo(0, 0); ctx!.lineTo(length, 0);
        ctx!.restore();
        // 45deg 2-side-lines
        if (angle === 45) {
          ctx!.moveTo(-size, 0); ctx!.lineTo(size, 2 * size);
          ctx!.moveTo(0, -size); ctx!.lineTo(2 * size, size);
        }
        // 135deg 2-side-lines
        if (angle === 135) {
          ctx!.moveTo(2 * size, 0); ctx!.lineTo(0, 2 * size);
          ctx!.moveTo(size, -size); ctx!.lineTo(-size, size);
        }
        ctx!.stroke();
        break;
      }
      case 'dots': {
        const radius = size / Math.sqrt(2) / 4;
        // 5 circles
        const offsets = [[0.5, 0.5], [0.5, 1.5], [1.5, 0], [1.5, 1], [1.5, 2]];
        offsets.forEach(([offsetX, offsetY]) => {
          ctx!.beginPath();
          ctx!.arc(offsetX * center, offsetY * center, radius, 0, 2 * Math.PI, false);
          ctx!.fill();
          ctx!.stroke();
        });

        // ONE CIRCLE
        // ctx!.beginPath();
        // ctx!.arc(center, center, radius, 0, 2 * Math.PI, false);
        // ctx!.fill();
        break;
      }
      case 'rhombs': {
        // 4 RHOMBs
        const offsets = [[0.5, 0], [0.5, 1],
        ...config.border ? [[1.5, 0]] : [],
        ...config.border ? [[1.5, 1]] : []
        ];
        offsets.forEach(([offsetX, offsetY]) => {
          ctx!.save();
          ctx!.translate(center * offsetX, center * offsetY);
          ctx!.rotate(45 * Math.PI / 180);
          const rectMethod = config.border ? 'rect' : 'fillRect';
          ctx![rectMethod](0, 0, size / (2 * Math.sqrt(2)), size / (2 * Math.sqrt(2)));
          ctx!.restore();
        });
        ctx!.stroke();
        break;
      }
    }

    return ctx!.getImageData(0, 0, canvas.width, canvas.height);
  }

  getLineSymbology(code: string): [string, string] {
    if (!code) {
      return ['#000', '#000'];
    }

    const [primary, ...secondary] = code.split(' ');
    for (const [p, c, d] of lineColorMap) {
      if (p === primary) {
        return [c, d];
      }
    }

    // Handling for line of trees
    if (primary.startsWith('w') && secondary.includes('33') || secondary.includes('34')) {
      return ['#339f2c', '#000000'];
    }
    // Fallback to base color
    const base = ukHabL2Colors[code[0] as keyof typeof ukHabL2Colors];
    return base ? [base, '#fff'] : ['#000', '#000'];
  }

  public ukhabBaselineFeatureStyler(): FeatureStyler {
    return (data: Feature[]) => data.map(f => {
      const code = this.ukhabPCode(f.properties);
      const symbology = this.matchUKHabSymbology(code);
      const properties = {
        ...f.properties,
        fillPattern: symbology
      };

      if (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString') {
        const [lineColor, lineDashColor] = this.getLineSymbology(code);
        properties['lineColor'] = lineColor;
        properties['lineDashColor'] = lineDashColor;
      }

      return {
        ...f,
        properties
      };
    });
  }

  public ukhabPreSurveyFeatureStyler(): FeatureStyler {
    return (data: Feature[]) => data.map(f => {
      const code = f.properties['uk_hab_level_2_dvyt5s'];
      const symbology = this.matchUKHabSymbology(code);
      const properties = {
        ...f.properties,
        fillPattern: symbology
      };
      if (f.geometry.type === 'LineString' || f.geometry.type === 'MultiLineString') {
        const [lineColor, lineDashColor] = this.getLineSymbology(code);
        properties['lineColor'] = lineColor;
        properties['lineDashColor'] = lineDashColor;
      }
      return {
        ...f,
        properties
      };
    });
  }

  public async loadSymbology(map: mapboxgl.Map) {
    for (const [key] of ukHabSybmology) {
      map.addImage(key, this.generateUKHabFillSymbology(key));
    }
  }

  public handleUKHab(map: mapboxgl.Map, id: string) {
    const match = this.matchUKHabSymbology(id);
    if(!match){
      return null;
    }
    if(!map.hasImage(id)){
      map.addImage(id, this.generateUKHabFillSymbology(id));
    }
    return id;
  }

}
